/**
 * Deterministic, render-only playback for a Live Analysis recommendation.
 *
 * A preview is driven exclusively by option.previewFrames.  The replay engine
 * remains paused; this module only returns temporary positions for rendering.
 * Coordinates are world coordinates and are never re-anchored to a canvas or
 * to a later player position.
 */

export const LIVE_PREVIEW_HZ = 60;
export const LIVE_PREVIEW_VERSION = 'live-preview-v1';
const DEFAULT_MAX_PREVIEW_TICKS = 3600;

const PHASES = new Set(['setup', 'release', 'flight', 'hold', 'carry', 'reassess', 'idle']);
const finite = value => typeof value === 'number' && Number.isFinite(value);

function point(value) {
  if (!value || !finite(value.x) || !finite(value.y)) return null;
  return { x: value.x, y: value.y };
}

function copy(value) {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return value.map(copy);
  if (!value || typeof value !== 'object') return value;
  return Object.keys(value).reduce((result, key) => {
    result[key] = copy(value[key]);
    return result;
  }, {});
}

function freeze(value, seen = new Set()) {
  if (!value || typeof value !== 'object' || seen.has(value)) return value;
  seen.add(value);
  Object.keys(value).forEach(key => freeze(value[key], seen));
  return Object.freeze(value);
}

function positionOf(value) {
  return point(value?.position || value?.pos || value);
}

function selectedPlayer(snapshot, playerId) {
  const players = Array.isArray(snapshot?.players) ? snapshot.players : [];
  return players.find(player => String(player?.id) === String(playerId))
    || players.find(player => String(player?.name || player?.nick || '').trim().toLowerCase() === String(playerId || '').trim().toLowerCase());
}

function invalid(reason, details = {}) {
  return { ok: false, reason, ...details };
}

/**
 * Validate and freeze a preview. `snapshot` is the paused authoritative state.
 * The first recorded frame must be tick 0 and must equal that state exactly.
 */
export function createLivePreviewSession(option, snapshot, options = {}) {
  const frames = Array.isArray(option?.previewFrames) ? option.previewFrames : null;
  if (!frames?.length) return invalid('missing-preview-frames');
  const player = selectedPlayer(snapshot, options.playerId ?? option.playerId ?? snapshot?.selectedPlayer);
  const startPlayer = positionOf(player);
  const startBall = positionOf(snapshot?.ball);
  if (!startPlayer || !startBall) return invalid('missing-start-state');
  const normalized = [];
  let previousTick = -1;
  const maxTicks = finite(options.maxTicks) ? Math.max(1, Math.floor(options.maxTicks)) : DEFAULT_MAX_PREVIEW_TICKS;
  for (let index = 0; index < frames.length; index += 1) {
    const frame = frames[index];
    const tickOffset = frame?.tickOffset;
    const framePlayer = point(frame?.player);
    const frameBall = point(frame?.ball);
    if (!finite(tickOffset) || tickOffset < 0 || Math.floor(tickOffset) !== tickOffset || tickOffset > maxTicks) return invalid('invalid-tick', { index });
    if (tickOffset <= previousTick) return invalid('nonmonotonic-ticks', { index });
    if (!framePlayer || !frameBall) return invalid('invalid-frame-position', { index });
    const phase = String(frame?.phase || 'idle').toLowerCase();
    if (!PHASES.has(phase)) return invalid('invalid-phase', { index, phase });
    normalized.push({ tickOffset, phase, player: framePlayer, ball: frameBall });
    previousTick = tickOffset;
  }
  const first = normalized[0];
  if (first.tickOffset !== 0) return invalid('first-frame-not-zero');
  if (first.player.x !== startPlayer.x || first.player.y !== startPlayer.y
    || first.ball.x !== startBall.x || first.ball.y !== startBall.y) {
    return invalid('start-mismatch', { expected: { player: startPlayer, ball: startBall }, received: first });
  }
  const startedAtMs = finite(options.startedAtMs) ? options.startedAtMs : Date.now();
  const session = {
    version: LIVE_PREVIEW_VERSION,
    playerId: options.playerId ?? option.playerId ?? player?.id ?? null,
    startedAtMs,
    durationMs: previousTick * 1000 / LIVE_PREVIEW_HZ,
    frames: normalized,
    start: { player: startPlayer, ball: startBall }
  };
  return { ok: true, session: freeze(session) };
}

function restore(session, reason, phase = 'idle') {
  return {
    active: false,
    done: true,
    reason,
    phase,
    tickOffset: 0,
    player: copy(session.start.player),
    ball: copy(session.start.ball),
    restore: { player: copy(session.start.player), ball: copy(session.start.ball) }
  };
}

function interpolate(a, b, fraction) {
  return {
    x: a.x + (b.x - a.x) * fraction,
    y: a.y + (b.y - a.y) * fraction
  };
}

/** Sample at a wall-clock time. The result is disposable render state. */
export function sampleLivePreviewSession(session, nowMs = Date.now()) {
  if (!session?.frames?.length || !session.start) return { active: false, done: true, reason: 'invalid-session' };
  if (session.cancelled) return restore(session, 'cancelled', 'idle');
  const elapsedMs = Number(nowMs) - Number(session.startedAtMs);
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) {
    const first = session.frames[0];
    return { active: true, done: false, reason: null, phase: first.phase, tickOffset: first.tickOffset, player: copy(first.player), ball: copy(first.ball) };
  }
  const tick = elapsedMs * LIVE_PREVIEW_HZ / 1000;
  const last = session.frames[session.frames.length - 1];
  if (tick >= last.tickOffset) return restore(session, 'complete', 'idle');
  let nextIndex = 1;
  while (nextIndex < session.frames.length && session.frames[nextIndex].tickOffset < tick) nextIndex += 1;
  const next = session.frames[nextIndex] || last;
  const previous = session.frames[nextIndex - 1] || session.frames[0];
  const span = next.tickOffset - previous.tickOffset || 1;
  const fraction = Math.max(0, Math.min(1, (tick - previous.tickOffset) / span));
  return {
    active: true,
    done: false,
    reason: null,
    phase: fraction >= 1 ? next.phase : previous.phase,
    tickOffset: tick,
    player: interpolate(previous.player, next.player, fraction),
    ball: interpolate(previous.ball, next.ball, fraction)
  };
}

/** Return a frozen cancelled copy; the source session is never changed. */
export function cancelLivePreviewSession(session) {
  if (!session?.frames?.length || !session.start) return session;
  return freeze({ ...copy(session), cancelled: true });
}

export function isLivePreviewSession(value) {
  return Boolean(value?.version === LIVE_PREVIEW_VERSION && Array.isArray(value.frames) && value.start);
}
